import React, { useEffect, useMemo, useState } from "react";
import { api, money } from "../../lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, BarChart, Bar, Legend,
} from "recharts";
import {
  Package, DollarSign, TrendingUp, XCircle, Award,
} from "lucide-react";
import { formatDayMonth } from "../../lib/format";
import { useCompany } from "../../contexts/CompanyContext";
import { useTheme } from "../../contexts/ThemeContext";

const STATUS_LABEL = {
  pending: "Recibido",
  confirmed: "Confirmado",
  on_the_way: "En camino",
  delivered: "Entregado",
  cancelled: "Cancelado",
};
const STATUS_COLOR = {
  pending: "#fbbf24",
  confirmed: "#3b82f6",
  on_the_way: "#8b5cf6",
  delivered: "#10b981",
  cancelled: "#ef4444",
};
const CHANNEL_LABEL = {
  autotanque: "Autotanque",
  cilindrera: "Cilindrera",
  estacion: "Estación",
  otro: "Otro",
};
const CHANNEL_COLORS = ["#0ea5e9", "#f97316", "#84cc16", "#a855f7"];

const RANGES = [
  { v: 7, t: "Últimos 7 días" },
  { v: 30, t: "Últimos 30 días" },
  { v: 90, t: "Últimos 90 días" },
  { v: 180, t: "Últimos 6 meses" },
];

const dayShort = (iso, tz) => formatDayMonth(iso, tz);

export default function AdminReports() {
  const { currentCompany } = useCompany();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  // Colores adaptados al tema. En dark usamos grises mucho más claros
  // para los axes/grid (porque el fondo es casi negro) y un stroke
  // ligero entre celdas del Pie en lugar de blanco puro.
  const chartGrid = isDark ? "#2d2d33" : "#eee";
  const chartAxis = isDark ? "#9ca3af" : "#888";
  const pieStroke = isDark ? "#0b0b0d" : "#fff";
  const tooltipStyle = isDark
    ? { borderRadius: 8, border: "1px solid #2d2d33", backgroundColor: "#18181b", color: "#e7e7e9" }
    : { borderRadius: 8, border: "1px solid #e5e5e5" };
  const tz = currentCompany?.timezone;
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/metrics/reports", { params: { days } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const perDay = data?.per_day || [];
  const statusPie = useMemo(() => {
    const map = data?.by_status || {};
    return Object.entries(map).map(([k, v]) => ({
      name: STATUS_LABEL[k] || k,
      value: v,
      color: STATUS_COLOR[k] || "#999",
    }));
  }, [data]);
  const channelBars = useMemo(() => {
    return (data?.by_channel || []).map((c, i) => ({
      name: CHANNEL_LABEL[c.channel] || c.channel,
      revenue: Math.round(c.revenue),
      qty: Math.round(c.qty),
      fill: CHANNEL_COLORS[i % CHANNEL_COLORS.length],
    }));
  }, [data]);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Reportes</h1>
          <p className="text-sm text-neutral-500 mt-1">Métricas de operación y ventas</p>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
          <SelectTrigger className="h-10 w-52" data-testid="reports-range-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {RANGES.map((r) => <SelectItem key={r.v} value={String(r.v)}>{r.t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* KPIs */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi icon={Package} label="Pedidos" value={data?.total_orders ?? "—"} />
        <Kpi icon={DollarSign} label="Ingresos" value={data ? money(data.total_revenue) : "—"} accent="emerald" />
        <Kpi icon={TrendingUp} label="Ticket promedio" value={data ? money(data.avg_ticket) : "—"} accent="primary" />
        <Kpi
          icon={XCircle}
          label="Tasa de cancelación"
          value={data ? `${data.cancellation_rate}%` : "—"}
          accent={data && data.cancellation_rate > 15 ? "red" : "neutral"}
        />
      </div>

      {/* Orders & revenue per day */}
      <div className="mt-6 bg-white rounded-2xl border border-neutral-200 p-5">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-display font-bold text-xl">Pedidos e ingresos por día</h2>
          <p className="text-xs text-neutral-500">Últimos {days} días</p>
        </div>
        <div className="mt-4" style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <AreaChart data={perDay} margin={{ left: 0, right: 12, top: 8, bottom: 0 }}>
              <defs>
                <linearGradient id="grOrders" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--company-primary)" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="var(--company-primary)" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="grRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} vertical={false} />
              <XAxis dataKey="date" tickFormatter={(v) => dayShort(v, tz)} stroke={chartAxis} fontSize={11} interval="preserveStartEnd" minTickGap={28} />
              <YAxis yAxisId="left" stroke={chartAxis} fontSize={11} allowDecimals={false} />
              <YAxis yAxisId="right" orientation="right" stroke={chartAxis} fontSize={11} tickFormatter={(v) => `$${v >= 1000 ? `${Math.round(v / 1000)}k` : v}`} />
              <Tooltip
                formatter={(value, name) => name === "Ingresos" ? money(value) : value}
                labelFormatter={(v) => dayShort(v, tz)}
                contentStyle={tooltipStyle}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area yAxisId="left" type="monotone" dataKey="orders" name="Pedidos" stroke="var(--company-primary)" strokeWidth={2} fill="url(#grOrders)" />
              <Area yAxisId="right" type="monotone" dataKey="revenue" name="Ingresos" stroke="#10b981" strokeWidth={2} fill="url(#grRevenue)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Status distribution + Channel revenue */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-neutral-200 p-5">
          <h2 className="font-display font-bold text-xl">Estado de pedidos</h2>
          <p className="text-xs text-neutral-500 mt-0.5">Distribución de los últimos {days} días</p>
          <div className="mt-4" style={{ width: "100%", height: 240 }}>
            {statusPie.length === 0 ? (
              <Empty />
            ) : (
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={statusPie} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={3}>
                    {statusPie.map((entry, idx) => (
                      <Cell key={idx} fill={entry.color} stroke={pieStroke} strokeWidth={2} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-neutral-200 p-5">
          <h2 className="font-display font-bold text-xl">Ingresos por canal</h2>
          <p className="text-xs text-neutral-500 mt-0.5">Suma del line_total por canal de venta</p>
          <div className="mt-4" style={{ width: "100%", height: 240 }}>
            {channelBars.length === 0 ? (
              <Empty />
            ) : (
              <ResponsiveContainer>
                <BarChart data={channelBars} margin={{ left: 0, right: 12, top: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} vertical={false} />
                  <XAxis dataKey="name" stroke={chartAxis} fontSize={11} />
                  <YAxis stroke={chartAxis} fontSize={11} tickFormatter={(v) => `$${v >= 1000 ? `${Math.round(v / 1000)}k` : v}`} />
                  <Tooltip formatter={(v) => money(v)} contentStyle={tooltipStyle} />
                  <Bar dataKey="revenue" name="Ingresos" radius={[6, 6, 0, 0]}>
                    {channelBars.map((c, idx) => <Cell key={idx} fill={c.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Top clients + Payment split */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-neutral-200 p-5">
          <h2 className="font-display font-bold text-xl flex items-center gap-2">
            <Award size={18} /> Top clientes
          </h2>
          <p className="text-xs text-neutral-500 mt-0.5">Mayor monto facturado en el período</p>
          <div className="mt-4 divide-y divide-neutral-100">
            {(data?.top_clients || []).map((c, idx) => (
              <div key={c.client_id || idx} className="py-2.5 flex items-center gap-3">
                <span className="h-8 w-8 rounded-full bg-company text-[color:var(--company-primary-contrast)] flex items-center justify-center font-bold text-sm shrink-0">
                  {idx + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{c.client_name}</p>
                  <p className="text-xs text-neutral-500">{c.orders} pedido{c.orders === 1 ? "" : "s"}</p>
                </div>
                <span className="font-display font-bold whitespace-nowrap">{money(c.revenue)}</span>
              </div>
            ))}
            {(!data?.top_clients || data.top_clients.length === 0) && <Empty />}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-neutral-200 p-5">
          <h2 className="font-display font-bold text-xl">Métodos de pago</h2>
          <p className="text-xs text-neutral-500 mt-0.5">Cantidad de pedidos por método</p>
          <div className="mt-4 space-y-2">
            {Object.entries(data?.by_payment || {}).map(([k, v]) => {
              const total = Object.values(data?.by_payment || {}).reduce((a, b) => a + b, 0) || 1;
              const pct = Math.round((v / total) * 100);
              return (
                <div key={k}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold capitalize">{k === "cash" ? "Efectivo" : k === "online" ? "En línea" : k}</span>
                    <span className="text-neutral-500">{v} <span className="text-xs text-neutral-400">({pct}%)</span></span>
                  </div>
                  <div className="h-2 bg-neutral-100 rounded-full overflow-hidden mt-1">
                    <div className="h-full bg-company" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
            {Object.keys(data?.by_payment || {}).length === 0 && <Empty />}
          </div>
        </div>
      </div>

      {loading && <p className="text-xs text-neutral-400 mt-4 text-center">Actualizando…</p>}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent = "neutral" }) {
  const tones = {
    neutral: "text-company",
    emerald: "text-emerald-600",
    red: "text-red-600",
    primary: "text-company",
  };
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-5">
      <Icon size={18} className={tones[accent]} />
      <p className="text-xs text-neutral-500 mt-3 uppercase tracking-widest">{label}</p>
      <p className="font-display font-extrabold text-2xl mt-1">{value}</p>
    </div>
  );
}

function Empty() {
  return <p className="text-sm text-neutral-400 text-center py-8">Sin datos en el período</p>;
}
